import { createServerFn } from "@tanstack/solid-start";
import { eq } from "drizzle-orm";
import { drizzle } from "drizzle-orm/node-postgres";
import { countBankConnectionsForUser } from "~/features/banking/banking.server";
import { countBusinessesForUser } from "~/features/business/business.server";
import { pool } from "~/lib/db";
import { traceRelational } from "~/lib/db-tracing";
import { withSentrySpan } from "~/lib/sentry-span";
import * as authSchema from "~/schemas/auth-schema";
import { user } from "~/schemas/auth-schema";
import {
  getAiCreditsUsedBySource,
  getAiCreditsUsedInPeriod,
} from "~/server/ai-billing";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import {
  getFreeTierStorageBytes,
  getIncludedAiCredits,
  getIncludedStorageBytes,
  getPlanByTier,
  hasActiveSubscription,
  type PlanTier,
} from "~/server/plans";
import { getUserStorageTotalBytes } from "~/server/s3";
import { syncSubscriptionFromStripe } from "~/server/stripe";

const db = drizzle(pool, { schema: authSchema });

export const getBillingStatus = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .handler(async ({ context }) => {
    return withSentrySpan("billing.getStatus", async () => {
      const dbUser = await traceRelational("billing.getStatus", () =>
        db.query.user.findFirst({
          where: eq(user.id, context.user.id),
        }),
      );
      // Safety net for missed Stripe webhook events — reconcile with the
      // live subscription before reporting.
      const synced = await syncSubscriptionFromStripe(dbUser);
      return {
        subscriptionStatus: synced.subscriptionStatus,
        subscriptionTier: synced.subscriptionTier,
        stripeSubscriptionId: dbUser?.stripeSubscriptionId ?? null,
        subscriptionCancelAt: synced.subscriptionCancelAt,
      };
    });
  });

export const getBillingUsage = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .handler(async ({ context }) => {
    return withSentrySpan("billing.getUsage", async () => {
      const dbUser = await traceRelational("billing.getUsage", () =>
        db.query.user.findFirst({
          where: eq(user.id, context.user.id),
        }),
      );
      // Reconcile with live Stripe first — same safety net as getBillingStatus.
      // Stripe retries failed webhook deliveries, so a stale event can write an
      // old tier to the DB after a plan change; without this, usage rows would
      // keep showing the previous plan's limits until a reconciling read.
      const synced = await syncSubscriptionFromStripe(dbUser);

      const isPaid = hasActiveSubscription(synced.subscriptionStatus);

      const tier: PlanTier = isPaid
        ? synced.subscriptionTier === "basic" ||
          synced.subscriptionTier === "pro" ||
          synced.subscriptionTier === "business"
          ? synced.subscriptionTier
          : "basic"
        : "free";

      const [
        storageBytes,
        aiCredits,
        businessCount,
        bankConnectionCount,
        aiCreditsBySource,
      ] = await Promise.all([
        getUserStorageTotalBytes(context.user.id).catch(() => 0),
        // Exact period credits from the ai_usage ledger — same source for
        // every tier (the Stripe meter only holds BILLABLE overage). Window:
        // Stripe billing period for paid, UTC calendar month for free.
        getAiCreditsUsedInPeriod(context.user.id).catch(() => 0),
        countBusinessesForUser(context.user.id).catch(() => 0),
        countBankConnectionsForUser(context.user.id).catch(() => 0),
        // Per-surface breakdown (agent / telegram / document extraction…)
        // for the AI settings usage card. Display only — fails to {}.
        getAiCreditsUsedBySource(context.user.id),
      ]);

      return {
        storageBytes,
        aiCredits,
        // Included allowance is a plan entitlement; usage beyond it is metered.
        aiCreditAllowance: getIncludedAiCredits(tier),
        storageQuotaBytes: isPaid
          ? getIncludedStorageBytes(tier)
          : getFreeTierStorageBytes(),
        // Business count is a plan entitlement — report it alongside the
        // plan's limit so the UI can show progress toward the cap.
        businessCount,
        businessLimit: getPlanByTier(tier)?.businessLimit ?? null,
        // Bank connections: included allowance is a plan entitlement; extra
        // connections are metered overage ($2/connection/mo).
        bankConnectionCount,
        bankConnectionLimit: getPlanByTier(tier)?.includedBankConnections ?? 0,
        aiCreditsBySource,
        tier,
      };
    });
  });
